// path: frontend/src/pages/AccountPage.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/firebase";
import { API_BASE_URL } from "@/config";
import { useAuth } from "@/context/AuthContext";
import toast from "react-hot-toast";

export default function AccountPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth() as any;
    const [confirm, setConfirm] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    if (!user) {
        navigate("/login");
        return null;
    }

    const handleDelete = async () => {
        if (confirm !== "DELETE") return;
        setDeleting(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/video/session/account`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j?.detail || `Failed (${res.status})`);
            }
            toast.success("Account deleted successfully");
            await logout();
            navigate("/");
        } catch (e: any) {
            toast.error(e?.message || "Failed to delete account");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-12 px-4">
            <div className="max-w-2xl mx-auto space-y-6">

                {/* Header */}
                <div>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-4 flex items-center gap-1"
                    >
                        ← Back
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Account Settings</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your AutoQuiz AI account</p>
                </div>

                {/* Profile card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Profile</h2>
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                            {user.email?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{user.displayName || "User"}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                Member since {user.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : "—"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Danger zone */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-900/50 p-6">
                    <h2 className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-1">Danger Zone</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Once you delete your account, there is no going back. All your videos, sessions, and data will be permanently removed.
                    </p>

                    {!showConfirm ? (
                        <button
                            onClick={() => setShowConfirm(true)}
                            className="px-4 py-2 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                            Delete my account
                        </button>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 text-sm text-red-700 dark:text-red-300 space-y-1.5">
                                <p className="font-semibold mb-2">This will permanently delete:</p>
                                <p>• All your generated videos and sessions</p>
                                <p>• Your account credentials and login access</p>
                                <p>• Your quota history and plan data</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm:
                                </label>
                                <input
                                    type="text"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                    placeholder="DELETE"
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowConfirm(false); setConfirm(""); }}
                                    className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={confirm !== "DELETE" || deleting}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {deleting ? "Deleting…" : "Permanently delete account"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}